import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "inboundBtn", "outboundBtn",
    "inboundCheck", "outboundCheck",
    "chart", "chartData",
    "legendDashed", "legendSolid",
    "statsCard", "partnersCard"
  ]

  connect() {
    if (this.hasChartTarget) {
      this.chartMode = "inbound"
      this.loadChartData()
      this.drawChart()
      this._resizeHandler = () => this.drawChart()
      window.addEventListener("resize", this._resizeHandler)
    }

    if (this.hasStatsCardTarget && this.hasPartnersCardTarget) {
      this.syncPartnersHeight()
      this._statsResizeObserver = new ResizeObserver(() => this.syncPartnersHeight())
      this._statsResizeObserver.observe(this.statsCardTarget)
      this._partnersResizeHandler = () => this.syncPartnersHeight()
      window.addEventListener("resize", this._partnersResizeHandler)
    }
  }

  disconnect() {
    if (this._resizeHandler) {
      window.removeEventListener("resize", this._resizeHandler)
    }
    if (this._statsResizeObserver) {
      this._statsResizeObserver.disconnect()
    }
    if (this._partnersResizeHandler) {
      window.removeEventListener("resize", this._partnersResizeHandler)
    }
  }

  syncPartnersHeight() {
    // Only sync on wide layouts where the cards sit side by side.
    if (window.innerWidth < 1401) {
      this.partnersCardTarget.style.height = ""
      return
    }
    const statsHeight = this.statsCardTarget.getBoundingClientRect().height
    if (statsHeight > 0) {
      this.partnersCardTarget.style.height = `${statsHeight}px`
    }
  }

  loadChartData() {
    const el = this.chartDataTarget
    this.chartDataValues = {
      inbound: {
        current: JSON.parse(el.dataset.inboundCurrent),
        last: JSON.parse(el.dataset.inboundLast)
      },
      outbound: {
        current: JSON.parse(el.dataset.outboundCurrent),
        last: JSON.parse(el.dataset.outboundLast)
      }
    }
  }

  selectChartInbound() {
    this.chartMode = "inbound"
    this.inboundBtnTarget.classList.add("active")
    this.outboundBtnTarget.classList.remove("active")
    this.inboundCheckTarget.classList.remove("d-none")
    this.outboundCheckTarget.classList.add("d-none")
    this.drawChart()
  }

  selectChartOutbound() {
    this.chartMode = "outbound"
    this.outboundBtnTarget.classList.add("active")
    this.inboundBtnTarget.classList.remove("active")
    this.outboundCheckTarget.classList.remove("d-none")
    this.inboundCheckTarget.classList.add("d-none")
    this.drawChart()
  }

  drawChart() {
    const canvas = this.chartTarget
    const ctx = canvas.getContext("2d")
    const dpr = window.devicePixelRatio || 1
    const rect = canvas.getBoundingClientRect()

    canvas.width = rect.width * dpr
    canvas.height = rect.height * dpr
    ctx.scale(dpr, dpr)

    const width = rect.width
    const height = rect.height

    ctx.clearRect(0, 0, width, height)

    const currentData = this.chartDataValues[this.chartMode].current
    const lastData = this.chartDataValues[this.chartMode].last
    const maxVal = Math.max(...currentData, ...lastData, 1)

    const padding = { top: 20, right: 20, bottom: 30, left: 40 }
    const chartWidth = width - padding.left - padding.right
    const chartHeight = height - padding.top - padding.bottom

    const lineColor = this.chartMode === "inbound" ? "#F1616E" : "#4168FF"

    // Update legend colors
    this.legendDashedTarget.style.borderColor = lineColor
    this.legendSolidTarget.style.backgroundColor = lineColor

    // Grid lines and Y-axis labels
    ctx.strokeStyle = "#E5E7EB"
    ctx.lineWidth = 1
    ctx.fillStyle = "#9CA3AF"
    ctx.font = "12px sans-serif"
    ctx.textAlign = "right"

    const yTicks = this.calculateYTicks(maxVal)
    const yMax = yTicks[yTicks.length - 1] || maxVal
    yTicks.forEach(val => {
      const y = padding.top + chartHeight - (val / yMax * chartHeight)
      ctx.beginPath()
      ctx.moveTo(padding.left, y)
      ctx.lineTo(width - padding.right, y)
      ctx.stroke()
      ctx.fillText(val, padding.left - 8, y + 4)
    })

    // X-axis labels
    ctx.textAlign = "center"
    ctx.fillStyle = "#9CA3AF"
    ;[1, 15, 31].forEach(day => {
      const x = padding.left + ((day - 1) / 30) * chartWidth
      ctx.fillText(day, x, height - 8)
    })

    // Last month line (solid)
    this.drawLine(ctx, lastData, yMax, padding, chartWidth, chartHeight, lineColor, false)
    // Current month line (dashed)
    this.drawLine(ctx, currentData, yMax, padding, chartWidth, chartHeight, lineColor, true)
  }

  drawLine(ctx, data, yMax, padding, chartWidth, chartHeight, color, dashed) {
    if (data.length < 2) return

    ctx.beginPath()
    ctx.strokeStyle = color
    ctx.lineWidth = 2.5
    ctx.setLineDash(dashed ? [6, 4] : [])

    const maxDays = 31
    data.forEach((val, i) => {
      const x = padding.left + (i / (maxDays - 1)) * chartWidth
      const y = padding.top + chartHeight - (val / yMax * chartHeight)
      if (i === 0) ctx.moveTo(x, y)
      else ctx.lineTo(x, y)
    })

    ctx.stroke()
    ctx.setLineDash([])
  }

  calculateYTicks(maxVal) {
    if (maxVal <= 5) return [0, 1, 2, 3, 4, 5].filter(v => v <= maxVal + 1)
    const step = Math.ceil(maxVal / 4)
    const ticks = []
    for (let i = 0; i <= maxVal + step; i += step) {
      ticks.push(i)
      if (ticks.length >= 5) break
    }
    return ticks
  }
}
